import {useCallback, useEffect, useMemo, useState} from 'react';

import {MeasurementUnit} from 'constants/__generated/MeasurementUnit';
import {DEFAULT_MEASUREMENT} from 'constants/MeasurementConstants';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {DEFAULT_DATA_FRAME_ROW_LIMIT} from 'data-frames/graphql/GetDataFrame';
import {
  GetPersistedDataFrame,
  GetPersistedDataFrame_dataFrame_InaccessibleInfoNode as InaccessibleInfoGraphQL,
  GetPersistedDataFrameVariables,
} from 'data-frames/wrapper/__generated/GetPersistedDataFrame';
import {
  GET_PERSISTED_DATA_FRAME_QUERY,
  PersistedDataFrameGraphQL,
} from 'data-frames/wrapper/PersistedDataFrameTableWrapper';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {PrimaryExperimentalRoleType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  GetScratchPlateCreatedPlates,
  GetScratchPlateCreatedPlatesVariables,
  GetScratchPlateCreatedPlates_scratchPlate_ScratchPlateNode_createdPlates as ScratchPlateCreatedPlate,
} from 'notebook/integrations/plate-maps/scratch-plates/__generated/GetScratchPlateCreatedPlates';
import {
  GetScratchPlateDesignSummary,
  GetScratchPlateDesignSummaryVariables,
  GetScratchPlateDesignSummary_scratchPlate_ScratchPlateNode as ScratchPlateNode,
} from 'notebook/integrations/plate-maps/scratch-plates/__generated/GetScratchPlateDesignSummary';
import {wellPositionKey} from 'notebook/integrations/plate-maps/scratch-plates/FilledScratchPlateUtil';
import {GET_SCRATCH_PLATE_CREATED_PLATES_QUERY} from 'notebook/integrations/plate-maps/scratch-plates/GetScratchPlateCreatedPlates';
import {GET_SCRATCH_PLATE_DESIGN_SUMMARY_QUERY} from 'notebook/integrations/plate-maps/scratch-plates/GetScratchPlateDesignSummary';
import {ExperimentalRole} from 'samples/experimental-roles/ExperimentalRoleConstants';
import {
  MetadataLayerInfo,
  ParameterFieldType,
  ParameterLayerInfo,
  PlateMap,
  PlateMapWell,
  SchematizedDisplayLayerMetadata,
} from 'samples/plate-maps/PlateMapConstants';
import {
  buildSchemaPlateFieldSystemName,
  deserializeSchemaTags,
  isPlateFieldSystemName,
  PlateMetadataFieldEntry,
  SerializedSchemaTag,
} from 'samples/plate-maps/scratch-plates/ScratchPlateDesignModalUtil';
import {getDefaultMeasurementWithErrors} from 'samples/plate-maps/transfer-table/validation/FillPlateTransferTableValidationUtil';
import * as SampleSchemaActions from 'samples/SampleSchemaActions';
import SampleSchemaStore from 'samples/SampleSchemaStore';
import {PlateSchema} from 'samples/storables/StorableSchemaConstants';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import {UnitsQuery, UnitsQueryVariables} from 'units/__generated/UnitsQuery';
import {UNITS_QUERY} from 'units/UnitsConstants';

export const LABEL_LAYER_NAME = 'label';
export const CONCENTRATION_DESIGN_LAYER_NAME = 'concentration_design';
export const METADATA_LAYER_NAME = 'metadata';
export const PARAMETER_LAYER_NAME = 'parameter';
export const DESTINATION_TRANSFER_LAYER_NAME = 'destination_transfer';

// How long to wait for SampleSchemaStore's plate-schema fetch before treating it as failed/hung.
// fetchAllPlateSchemas() doesn't surface success or failure to its caller, so this is a heuristic, not
// an exact signal — but the request is a small, same-origin GET that normally resolves in well under
// a second, so a real failure is reliably distinguishable from "still loading" at this threshold.
export const SCHEMA_STORE_LOAD_TIMEOUT_MS = 10_000;

type DataFrameRef = Parameters<typeof getAccessibleOrInaccessibleId>[0];

/** Extract the data frame id from a layer's `dataFrame` node, or null if the layer is absent. */
export function layerDataFrameId(layer: {dataFrame: DataFrameRef} | undefined): string | null {
  return layer != null ? getAccessibleOrInaccessibleId(layer.dataFrame) : null;
}

/**
 * Extract the plate schema id from a scratch plate's `plateSchema` node, or null if unset.
 *
 * `plateSchema` is a GridSchema union, so it may in principle be a non-plate grid schema whose id
 * this query doesn't select; only a PlateSchemaNode (or the inaccessible fallback) carries an id.
 */
export function scratchPlateSchemaId(plate: {
  plateSchema:
    | {__typename: 'PlateSchemaNode'; id: string}
    | {__typename: 'InaccessibleInfoNode'; invalidId: string}
    | {__typename: 'BoxSchemaNode'}
    | null;
}): string | null {
  const {plateSchema} = plate;
  if (plateSchema == null) {
    return null;
  }
  if (plateSchema.__typename === 'PlateSchemaNode') {
    return plateSchema.id;
  }
  if (plateSchema.__typename === 'InaccessibleInfoNode') {
    return plateSchema.invalidId;
  }
  return null;
}

function isPersistedDataFrame(
  item: {__typename?: string} | null | undefined
): item is PersistedDataFrameGraphQL {
  return item?.__typename === 'DataFrameNode';
}

function useDataFrame(dataFrameId: string | null): {
  dataFrame: PersistedDataFrameGraphQL | InaccessibleInfoGraphQL | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const result = useSkippableBenchlingQuery<GetPersistedDataFrame, GetPersistedDataFrameVariables>(
    GET_PERSISTED_DATA_FRAME_QUERY,
    {
      variables: {dataFrameId: dataFrameId ?? '', limit: DEFAULT_DATA_FRAME_ROW_LIMIT},
      skip: dataFrameId == null,
    }
  );
  const {refetch} = result;

  if (dataFrameId == null) {
    return {dataFrame: undefined, isLoading: false, isError: false, refetch};
  }
  if (result.type === QueryResultType.PENDING) {
    return {dataFrame: undefined, isLoading: true, isError: false, refetch};
  }
  if (result.type === QueryResultType.ERROR) {
    return {dataFrame: undefined, isLoading: false, isError: true, refetch};
  }
  if (result.type !== QueryResultType.SUCCESS) {
    return {dataFrame: undefined, isLoading: false, isError: false, refetch};
  }
  if (isInaccessible(result.data.dataFrame)) {
    return {dataFrame: result.data.dataFrame, isLoading: false, isError: false, refetch};
  }
  if (isPersistedDataFrame(result.data.dataFrame)) {
    return {dataFrame: result.data.dataFrame, isLoading: false, isError: false, refetch};
  }
  return {dataFrame: undefined, isLoading: false, isError: false, refetch};
}
interface ConcentrationLayerRow {
  layerName: string;
  rowIndex: number;
  columnIndex: number;
  amount: number | null;
  unitApiId: string;
  unitVersionNumber: number;
}

interface ParsedConcentrationDesign {
  rows: Array<ConcentrationLayerRow>;
  unitApiIds: Array<string>;
}

/** Extract row data and unit IDs from the `concentration_design` data frame. */
function parseConcentrationDesignDataFrame(dataFrame: DataFrame): ParsedConcentrationDesign {
  const rows: Array<ConcentrationLayerRow> = [];
  const unitApiIdsSet: Set<string> = new Set();

  for (const dataFrameRow of dataFrame.rows) {
    if (dataFrameRow._concentration_unit_api_id == null) {
      continue;
    }

    const unitApiId = dataFrameRow._concentration_unit_api_id as string;
    rows.push({
      layerName: dataFrameRow._concentration_layer_name as string,
      rowIndex: dataFrameRow._row_index as number,
      columnIndex: dataFrameRow._column_index as number,
      amount:
        dataFrameRow._concentration_amount != null
          ? parseFloat(dataFrameRow._concentration_amount as string)
          : null,
      unitApiId,
      unitVersionNumber: dataFrameRow._concentration_unit_version_number as number,
    });
    unitApiIdsSet.add(unitApiId);
  }
  return {
    rows,
    unitApiIds: Array.from(unitApiIdsSet),
  };
}

interface ConcentrationDesignInformation {
  rows: Array<ConcentrationLayerRow>;
  // Hydrated mapping of unit ID to unit information
  unitsByApiId: Map<string, MeasurementUnit>;
}

/** Extract the unique volume unit API IDs referenced in the label data frame. */
function parseVolumeUnitApiIdsFromLabelDataFrame(dataFrame: DataFrame): Array<string> {
  const ids = new Set<string>();
  for (const row of dataFrame.rows) {
    const id = row._well_volume_unit_api_id;
    if (id != null && id !== '') {
      ids.add(id as string);
    }
  }
  return Array.from(ids);
}

/** Hydrate units referenced by the concentration design and label (volume) data frames. */
function useUnitInformation(
  concentrationDataFrame: PersistedDataFrameGraphQL | undefined,
  labelDataFrame: PersistedDataFrameGraphQL | undefined
): ConcentrationDesignInformation & {isLoading: boolean} {
  const {rows, unitApiIds: concentrationUnitApiIds} = useMemo((): ParsedConcentrationDesign => {
    if (concentrationDataFrame?.dataFrameJson == null) {
      return {
        rows: [],
        unitApiIds: [],
      };
    }
    return parseConcentrationDesignDataFrame(concentrationDataFrame.dataFrameJson);
  }, [concentrationDataFrame]);

  const volumeUnitApiIds = useMemo((): Array<string> => {
    if (labelDataFrame?.dataFrameJson == null) {
      return [];
    }
    return parseVolumeUnitApiIdsFromLabelDataFrame(labelDataFrame.dataFrameJson);
  }, [labelDataFrame]);

  const unitApiIds = useMemo(
    () => [...new Set([...concentrationUnitApiIds, ...volumeUnitApiIds])],
    [concentrationUnitApiIds, volumeUnitApiIds]
  );

  const unitsResult = useSkippableBenchlingQuery<UnitsQuery, UnitsQueryVariables>(UNITS_QUERY, {
    variables: {ids: unitApiIds},
    skip: unitApiIds.length === 0,
  });
  const unitsByApiId = useMemo((): Map<string, MeasurementUnit> => {
    if (unitsResult.type !== QueryResultType.SUCCESS) {
      return new Map();
    }
    return new Map(unitsResult.data.units.map((u) => [u.id, u as unknown as MeasurementUnit]));
  }, [unitsResult]);

  return {
    rows,
    unitsByApiId,
    isLoading: unitApiIds.length > 0 && unitsResult.type === QueryResultType.PENDING,
  };
}

// Keep aligned with METADATA_ALLOWED_LEAF_TYPES in MetadataLayerSettings so authored layer types
// round-trip on edit. DATETIME is intentionally excluded for now (see that file).
const METADATA_ALLOWED_LEAF_TYPES_SET = new Set<LeafType>([
  LeafType.TEXT,
  LeafType.INTEGER,
  LeafType.DECIMAL,
  LeafType.BOOLEAN,
  LeafType.DATE,
]);

export function leafTypeToMetadataType(leafType: LeafType): LeafType {
  return METADATA_ALLOWED_LEAF_TYPES_SET.has(leafType) ? leafType : LeafType.TEXT;
}

function columnsToLayerInfo(cols: DataFrame['columns']): Array<MetadataLayerInfo> {
  return cols.map((col) => ({
    layerId: col.systemName,
    name: col.displayName,
    type: leafTypeToMetadataType(col.type.leafType),
  }));
}

interface PartitionedMetadataColumns {
  wellLayerColumns: DataFrame['columns'];
  plateFieldColumns: DataFrame['columns'];
}

/** Splits the metadata layer's columns into per-well layers and whole-plate fields in a single pass. */
function partitionMetadataColumns(allColumnsJson: DataFrame['columns']): PartitionedMetadataColumns {
  const wellLayerColumns: DataFrame['columns'] = [];
  const plateFieldColumns: DataFrame['columns'] = [];
  for (const col of allColumnsJson) {
    if (col.systemName.startsWith('_')) {
      continue;
    }
    (isPlateFieldSystemName(col.systemName) ? plateFieldColumns : wellLayerColumns).push(col);
  }
  return {wellLayerColumns, plateFieldColumns};
}

export function parseMetadataLayerInfo(allColumnsJson: DataFrame['columns']): Array<MetadataLayerInfo> {
  return columnsToLayerInfo(partitionMetadataColumns(allColumnsJson).wellLayerColumns);
}

// A design's persisted plate fields are shaped identically to `PlateMetadataFieldEntry` so they can
// seed that modal's edit state with no remapping.
export type PlateFieldValue = PlateMetadataFieldEntry;

/** The counterpart to `parseMetadataLayerInfo` — keeps only plate-level ("plate_field_") columns. */
export function parsePlateFieldInfo(allColumnsJson: DataFrame['columns']): Array<MetadataLayerInfo> {
  return columnsToLayerInfo(partitionMetadataColumns(allColumnsJson).plateFieldColumns);
}

/**
 * Reads each plate field's single value off the data frame's first row — the backend duplicates a
 * plate field's value onto every well row, so any row carries the same value.
 */
export function parsePlateFieldValues(
  dataFrame: DataFrame,
  plateFields: Array<MetadataLayerInfo>
): Array<PlateFieldValue> {
  const firstRow = dataFrame.rows[0];
  return plateFields.map((field) => ({
    id: field.layerId,
    name: field.name,
    type: field.type,
    value: firstRow != null ? coerceMetadataValue(firstRow[field.layerId], field.type) : null,
  }));
}

function coerceMetadataValue(raw: unknown, leafType: LeafType): string | number | boolean | null {
  if (raw == null || (typeof raw === 'string' && raw.trim() === '')) {
    return null;
  }
  if (leafType === LeafType.INTEGER) {
    const parsed = typeof raw === 'number' ? raw : Number(String(raw));
    return Number.isInteger(parsed) ? parsed : null;
  }
  if (leafType === LeafType.DECIMAL) {
    const parsed = typeof raw === 'number' ? raw : Number.parseFloat(String(raw));
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (leafType === LeafType.BOOLEAN) {
    if (typeof raw === 'boolean') {
      return raw;
    }
    const normalized = String(raw).trim().toLowerCase();
    if (normalized === 'true') {
      return true;
    }
    if (normalized === 'false') {
      return false;
    }
    return null;
  }

  // TEXT / DATE / DATETIME are consumed as string render values in display layers.
  return String(raw);
}

export function parseMetadataValues(
  dataFrame: DataFrame,
  layers: Array<MetadataLayerInfo>
): Map<string, Record<string, string | number | boolean | null>> {
  const result = new Map<string, Record<string, string | number | boolean | null>>();
  for (const row of dataFrame.rows) {
    const posKey = wellPositionKey(row._row_index as number, row._column_index as number);
    const values: Record<string, string | number | boolean | null> = {};
    for (const layer of layers) {
      values[layer.layerId] = coerceMetadataValue(row[layer.layerId], layer.type);
    }
    result.set(posKey, values);
  }
  return result;
}

/**
 * Parse the distinct parameter layers from the parameter data frame rows. Each layer's identity is
 * frozen on every row (`_parameter_version_id`, `_parameter_name`, `_parameter_field_type`); the
 * layerId is the parameter version id.
 */
export function parseParameterLayerInfo(rows: DataFrame['rows']): Array<ParameterLayerInfo> {
  const byVersionId = new Map<string, ParameterLayerInfo>();
  for (const row of rows) {
    const versionId = row._parameter_version_id as string | null;
    if (!versionId || byVersionId.has(versionId)) {
      continue;
    }
    const fieldTypeRaw = String(row._parameter_field_type ?? ParameterFieldType.TEXT);
    const fieldType =
      fieldTypeRaw === ParameterFieldType.FLOAT
        ? ParameterFieldType.FLOAT
        : fieldTypeRaw === ParameterFieldType.DATETIME
          ? ParameterFieldType.DATETIME
          : ParameterFieldType.TEXT;
    byVersionId.set(versionId, {
      layerId: versionId,
      name: (row._parameter_name as string | null) ?? versionId,
      fieldType,
    });
  }
  return Array.from(byVersionId.values());
}

export function parseParameterValues(
  rows: DataFrame['rows'],
  layers: Array<ParameterLayerInfo>
): Map<string, Record<string, string | number | null>> {
  const fieldTypeByLayerId = new Map(layers.map((l) => [l.layerId, l.fieldType]));
  const result = new Map<string, Record<string, string | number | null>>();
  for (const row of rows) {
    const versionId = row._parameter_version_id as string | null;
    if (!versionId || !fieldTypeByLayerId.has(versionId)) {
      continue;
    }
    const fieldType = fieldTypeByLayerId.get(versionId);
    const isFloat = fieldType === ParameterFieldType.FLOAT;
    const isDatetime = fieldType === ParameterFieldType.DATETIME;
    const raw = isFloat
      ? row._parameter_value_float
      : isDatetime
        ? row._parameter_value_datetime
        : row._parameter_value_text;
    let value: string | number | null;
    if (raw == null || (typeof raw === 'string' && raw.trim() === '')) {
      value = null;
    } else if (isFloat) {
      const parsed = typeof raw === 'number' ? raw : Number.parseFloat(String(raw));
      value = Number.isFinite(parsed) ? parsed : null;
    } else {
      value = String(raw);
    }
    if (value == null) {
      continue;
    }
    const posKey = wellPositionKey(row._row_index as number, row._column_index as number);
    const values = result.get(posKey) ?? {};
    values[versionId] = value;
    result.set(posKey, values);
  }
  return result;
}

function parseRoleFromLabelRow(
  roleStr: string | null,
  subrole: string | null,
  groupRaw: unknown
): ExperimentalRole | null {
  // The label layer stores `_group` as free text (e.g. a Fill plate "group" column), so non-numeric
  // values fall back to group 1, matching how the backend applies label rows.
  const group = groupRaw == null ? 0 : Number.isNaN(Number(groupRaw)) ? 1 : Number(groupRaw);
  if (
    roleStr != null &&
    Object.values(PrimaryExperimentalRoleType).includes(roleStr as PrimaryExperimentalRoleType)
  ) {
    return {
      primaryRole: roleStr as PrimaryExperimentalRoleType,
      subrole,
      group,
    };
  }
  return null;
}

/** Build the PlateMap object that is used by front-end components. */
export function buildPlateMap(
  plate: ScratchPlateNode,
  labelDataFrame: DataFrame | undefined,
  concentrationRows: Array<ConcentrationLayerRow>,
  unitsByApiId: Map<string, MeasurementUnit>,
  metadataLayers?: Array<MetadataLayerInfo>,
  metadataValuesByWell?: Map<string, Record<string, string | number | boolean | null>>,
  parameterLayers?: Array<ParameterLayerInfo>,
  parameterValuesByWell?: Map<string, Record<string, string | number | null>>
): PlateMap {
  // Build a map from layerName -> layerId (generated from position in unique layer names)
  const layerNames = [...new Set(concentrationRows.map((r) => r.layerName))];
  // Use layer name as the layerId key (consistent with how the modal names layers)
  const layerIdByName = new Map(layerNames.map((name) => [name, `persisted-layer-${name}`]));

  // Build per-well concentration map: (row,col) -> {layerId -> Measurement}
  const concentrationByWellPos = new Map<
    string,
    Map<string, {value: number | null; unit: MeasurementUnit | null}>
  >();
  for (const row of concentrationRows) {
    const posKey = wellPositionKey(row.rowIndex, row.columnIndex);
    if (!concentrationByWellPos.has(posKey)) {
      concentrationByWellPos.set(posKey, new Map());
    }
    const layerId = layerIdByName.get(row.layerName)!;
    const unit = unitsByApiId.get(row.unitApiId) ?? null;
    concentrationByWellPos.get(posKey)!.set(layerId, {value: row.amount, unit});
  }

  // Group label rows by (row, column) — label frames may emit multiple rows per well (one per role).
  // We use the first row's role and deduplicate to one row per position.
  const labelRowsByPosition = new Map<string, DataFrame['rows'][number]>();
  for (const row of labelDataFrame?.rows ?? []) {
    const rowIndex = row._row_index as number | null;
    const columnIndex = row._column_index as number | null;
    if (rowIndex == null || columnIndex == null) {
      continue;
    }
    const posKey = wellPositionKey(rowIndex, columnIndex);
    if (!labelRowsByPosition.has(posKey)) {
      labelRowsByPosition.set(posKey, row);
    }
  }

  const wells: Array<PlateMapWell> = [];
  for (let rowIndex = 0; rowIndex < plate.height; rowIndex += 1) {
    for (let columnIndex = 0; columnIndex < plate.width; columnIndex += 1) {
      const posKey = wellPositionKey(rowIndex, columnIndex);
      const labelRow = labelRowsByPosition.get(posKey);
      const roleStr = (labelRow?._role as string | null | undefined) ?? null;
      const subrole = (labelRow?._subrole as string | null | undefined) ?? null;
      const roleFromLabel = parseRoleFromLabelRow(roleStr, subrole, labelRow?._group);
      const role = roleFromLabel ?? null;

      const wellConcentrations = concentrationByWellPos.get(posKey) ?? new Map();
      const concentrationLayers: PlateMapWell['concentrationLayers'] = {};
      for (const [layerId, measurement] of wellConcentrations) {
        if (measurement.value != null && measurement.unit != null) {
          concentrationLayers[layerId] = {value: measurement.value, unit: measurement.unit};
        }
      }

      const schemaTags = deserializeSchemaTags(
        (labelRow?._schema_tags as Record<string, SerializedSchemaTag> | null | undefined) ?? null
      );

      const wellMetadata = metadataValuesByWell?.get(posKey) ?? {};
      const wellParameters = parameterValuesByWell?.get(posKey) ?? {};

      const rawVolumeAmount = labelRow?._well_volume_amount;
      const volumeAmount = rawVolumeAmount != null && rawVolumeAmount !== '' ? Number(rawVolumeAmount) : null;
      const volumeUnitApiId = (labelRow?._well_volume_unit_api_id as string | null | undefined) ?? null;
      const volumeUnit = volumeUnitApiId != null ? (unitsByApiId.get(volumeUnitApiId) ?? null) : null;
      const volumeMeasurement =
        volumeAmount != null || volumeUnit != null
          ? {value: volumeAmount, unit: volumeUnit}
          : DEFAULT_MEASUREMENT;

      wells.push({
        position: {row: rowIndex, column: columnIndex},
        role,
        sources: [] as PlateMapWell['sources'],
        contents: [] as PlateMapWell['contents'],
        schemaTags,
        dataParameterTags: {} as PlateMapWell['dataParameterTags'],
        quantity: volumeMeasurement,
        finalQuantity: getDefaultMeasurementWithErrors(),
        concentrationLayers,
        metadataLayers: Object.keys(wellMetadata).length > 0 ? wellMetadata : undefined,
        parameterLayers: Object.keys(wellParameters).length > 0 ? wellParameters : undefined,
      });
    }
  }

  const concentrationLayerInfoById =
    layerNames.length > 0
      ? Object.fromEntries(
          layerNames.map((name) => {
            const layerId = layerIdByName.get(name)!;
            return [layerId, {layerId, name}];
          })
        )
      : undefined;

  const metadataLayerInfoById: {[layerId: string]: MetadataLayerInfo} | undefined =
    metadataLayers && metadataLayers.length > 0
      ? Object.fromEntries(metadataLayers.map((l) => [l.layerId, l]))
      : undefined;

  const parameterLayerInfoById: {[layerId: string]: ParameterLayerInfo} | undefined =
    parameterLayers && parameterLayers.length > 0
      ? Object.fromEntries(parameterLayers.map((l) => [l.layerId, l]))
      : undefined;

  return {
    id: plate.id,
    size: {numRows: plate.height, numColumns: plate.width},
    wells,
    concentrationLayerInfoById,
    metadataLayerInfoById,
    parameterLayerInfoById,
  };
}

interface ScratchPlateData {
  // Underlying GraphQL query state; callers branch on `result.type` and
  // `isInaccessible(result.data.scratchPlate)` rather than a hand-rolled status enum.
  scratchPlateQuery: ReturnType<
    typeof useSkippableBenchlingQuery<GetScratchPlateDesignSummary, GetScratchPlateDesignSummaryVariables>
  >;
  // Result of GET_SCRATCH_PLATE_DESIGN_SUMMARY_QUERY
  plate: ScratchPlateNode | null;
  // Object that is recognized by our client-side plate components; essentially a translation of `plate`
  plateMap: PlateMap | null;
  // If `plate` has a linked plate schema, then this is the plate's owning container schema's ID
  containerSchemaId: string | null;
  // The plate's linked plate schema, if any.
  plateSchema?: PlateSchema | null;
  // True if the current user lacks permission to view one or more of the plate's backing data frames
  dataFrameIsInaccessible: boolean;
  // True while the plate's backing data frame queries are still loading.
  dataFrameQueriesPending: boolean;
  // True if one of the plate's backing data frame queries failed.
  dataFrameQueriesError: boolean;
  // True until SampleSchemaStore's plate schemas have loaded at least once. `plateSchema` reads as
  // null both when the plate genuinely has no schema and when the store hasn't loaded yet, so callers
  // that write `plateSchema` back (e.g. filling a design) must wait for this to go false first.
  schemaStorePending: boolean;
  // True once `schemaStorePending` has stayed true past SCHEMA_STORE_LOAD_TIMEOUT_MS, a heuristic for
  // "the schema fetch appears to have failed or hung" — fetchAllPlateSchemas() doesn't itself report
  // success/failure to its caller. Always false once the store actually loads.
  schemaStoreLoadFailed: boolean;
  // Ad hoc plate-level ("whole plate") metadata fields, e.g. those set via
  // ScratchPlateDesignModal's plate-metadata-fields UI when no schema is selected — distinct from
  // `plateMap.metadataLayerInfoById`'s per-well layers. Disjoint from `plateSchemaFieldValues`.
  plateMetadataFields?: Array<PlateFieldValue>;
  // Plate-level values for the selected plate schema's own fields — disjoint from
  // `plateMetadataFields`; populated only when the plate has a schema.
  plateSchemaFieldValues?: Array<PlateFieldValue>;
  // Callback to fetch `plate` again
  refetch: () => void;
  // Callback to retry the label/concentration_design/metadata/parameter data-frame queries after a
  // `dataFrameQueriesError`. Independent of `refetch`, which only re-fetches `plate` itself.
  refetchDataFrames: () => void;
}

/**
 * Fetch the ScratchPlate object and its `label`, `concentration_design`, and optional
 * `metadata` and `parameter` data frame layers to assemble a full PlateMap.
 */
export function useScratchPlateData(scratchPlateId: string): ScratchPlateData {
  const scratchPlateQuery = useSkippableBenchlingQuery<
    GetScratchPlateDesignSummary,
    GetScratchPlateDesignSummaryVariables
  >(GET_SCRATCH_PLATE_DESIGN_SUMMARY_QUERY, {variables: {id: scratchPlateId}});

  // plate + available data frame layers have all information necessary to render the plate
  const plate: ScratchPlateNode | null =
    scratchPlateQuery.type === QueryResultType.SUCCESS &&
    scratchPlateQuery.data.scratchPlate.__typename === 'ScratchPlateNode'
      ? scratchPlateQuery.data.scratchPlate
      : null;
  const labelDataFrameId = layerDataFrameId(plate?.layers.find((l) => l.name === LABEL_LAYER_NAME));
  const {
    dataFrame: labelDataFrame,
    isLoading: labelDataFrameIsLoading,
    isError: labelDataFrameIsError,
    refetch: refetchLabelDataFrame,
  } = useDataFrame(labelDataFrameId);
  const concentrationDataFrameId = layerDataFrameId(
    plate?.layers.find((l) => l.name === CONCENTRATION_DESIGN_LAYER_NAME)
  );
  const {
    dataFrame: concentrationDataFrame,
    isLoading: concentrationDataFrameIsLoading,
    isError: concentrationDataFrameIsError,
    refetch: refetchConcentrationDataFrame,
  } = useDataFrame(concentrationDataFrameId);
  const metadataDataFrameId = layerDataFrameId(plate?.layers.find((l) => l.name === METADATA_LAYER_NAME));
  const {
    dataFrame: metadataDataFrame,
    isLoading: metadataDataFrameIsLoading,
    isError: metadataDataFrameIsError,
    refetch: refetchMetadataDataFrame,
  } = useDataFrame(metadataDataFrameId);
  const parameterDataFrameId = layerDataFrameId(plate?.layers.find((l) => l.name === PARAMETER_LAYER_NAME));
  const {
    dataFrame: parameterDataFrame,
    isLoading: parameterDataFrameIsLoading,
    isError: parameterDataFrameIsError,
    refetch: refetchParameterDataFrame,
  } = useDataFrame(parameterDataFrameId);
  // Aggregates the four independently-fetched data-frame queries so the "Failed to load this plate
  // design" retry action can recover from a `dataFrameQueriesError`: refetching the parent
  // `scratchPlateQuery` alone wouldn't re-trigger these, since their variables (the data frame ids)
  // wouldn't have changed. Optional layers (metadata/parameter) are often absent, in which case
  // `useDataFrame` skips their query rather than issuing it with a real id — calling `refetch` on a
  // skipped query would still execute it, against the placeholder `dataFrameId: ''` variables it was
  // skipped with, so each is only refetched when its layer actually has an id.
  function refetchDataFrames(): void {
    if (labelDataFrameId != null) {
      refetchLabelDataFrame();
    }
    if (concentrationDataFrameId != null) {
      refetchConcentrationDataFrame();
    }
    if (metadataDataFrameId != null) {
      refetchMetadataDataFrame();
    }
    if (parameterDataFrameId != null) {
      refetchParameterDataFrame();
    }
  }

  // Translate concentration data frame into row data and hydrate its unit references
  const {
    rows: concentrationRows,
    unitsByApiId,
    isLoading: unitsIsLoading,
  } = useUnitInformation(
    isInaccessible(concentrationDataFrame) ? undefined : concentrationDataFrame,
    isInaccessible(labelDataFrame) ? undefined : labelDataFrame
  );

  const dataFrameIsInaccessible =
    isInaccessible(labelDataFrame) ||
    isInaccessible(concentrationDataFrame) ||
    isInaccessible(metadataDataFrame) ||
    isInaccessible(parameterDataFrame);
  const dataFrameQueriesPending =
    labelDataFrameIsLoading ||
    concentrationDataFrameIsLoading ||
    metadataDataFrameIsLoading ||
    parameterDataFrameIsLoading ||
    unitsIsLoading;
  const dataFrameQueriesError =
    labelDataFrameIsError ||
    concentrationDataFrameIsError ||
    metadataDataFrameIsError ||
    parameterDataFrameIsError;

  const accessibleLabelDataFrame = isInaccessible(labelDataFrame) ? undefined : labelDataFrame;
  const accessibleMetadataDataFrame = isInaccessible(metadataDataFrame) ? undefined : metadataDataFrame;
  const accessibleParameterDataFrame = isInaccessible(parameterDataFrame) ? undefined : parameterDataFrame;

  // SampleSchemaStore populates asynchronously and independently of this query, so deriving
  // `plateSchema` has to react to *two* independent triggers: the store loading (after `plate`
  // already resolved) and `plate` resolving (after the store already loaded). `useStoreSelector`
  // alone only covers the first: its state is seeded once on mount and thereafter only replaced on
  // the store's own change event, so if `plateSchemas` was already loaded by the time `plateSchemaId`
  // went from null to a real id, no later change event would ever arrive to pick that up. Selecting
  // the raw `plateSchemas` array (which *does* update on every store change) as a plain reactive
  // dependency, then re-deriving `plateSchema` in a memo keyed on both it and `plateSchemaId`, reacts
  // correctly to either trigger.
  const plateSchemaId = plate != null ? scratchPlateSchemaId(plate) : null;
  const plateSchemas = useStoreSelector(
    SampleSchemaStore,
    useCallback((store) => store.plateSchemas, [])
  );
  const plateSchema = useMemo(
    () => (plateSchemaId != null ? (plateSchemas?.find(({id}) => id === plateSchemaId) ?? null) : null),
    [plateSchemaId, plateSchemas]
  );
  // Only wait when the design actually references a schema — a schema-less design has nothing to
  // resolve here and shouldn't be blocked by an unrelated (and possibly still-loading or failing)
  // global schema-list fetch.
  const schemaStorePending = plateSchemaId != null && plateSchemas == null;
  // Nothing guarantees plate schemas have been fetched before this widget mounts (unlike, say,
  // opening a schema selector elsewhere), so trigger the load here too.
  const [hasTimedOutWaitingForSchemaStore, setHasTimedOutWaitingForSchemaStore] = useState(false);
  useEffect(() => {
    if (SampleSchemaStore.plateSchemas != null) {
      return undefined;
    }
    // fetchAllPlateSchemas() no-ops if a request is already in flight, so every scratch-plate widget
    // mounted on the same page can safely call it and just wait on the same store update.
    SampleSchemaActions.fetchAllPlateSchemas();
    const timeoutId = window.setTimeout(() => {
      if (SampleSchemaStore.plateSchemas == null) {
        setHasTimedOutWaitingForSchemaStore(true);
      }
    }, SCHEMA_STORE_LOAD_TIMEOUT_MS);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, []);
  // True if the design references a schema that never resolved to a real value: either the store's
  // own fetch appears to have failed or hung (the timeout above), or the list loaded but didn't
  // contain that schema id (e.g. it was deleted) — in both cases `plateSchema` can't be trusted to
  // reflect the design's real schema, so this must block the same way `schemaStorePending` does.
  // Both clauses self-correct if the store later loads the schema.
  const schemaNotFoundInLoadedList = plateSchemaId != null && plateSchemas != null && plateSchema == null;
  const schemaStoreLoadFailed =
    (hasTimedOutWaitingForSchemaStore && schemaStorePending) || schemaNotFoundInLoadedList;

  // Parses the metadata layer's columns once, partitioning them into per-well layers and
  // whole-plate fields in a single pass rather than filtering the same column list twice. Plate
  // fields are further split by cross-referencing each column's system name against the plate's
  // current schema fields (see buildSchemaPlateFieldSystemName) — a match is a schema field value,
  // anything else is a true ad hoc field. The two sets are disjoint by construction on write, but a
  // schema change between write and read (e.g. an in-flight edit elsewhere) could in principle leave
  // a stale schema-shaped column around, which this treats as ad hoc rather than misattributing it.
  const {parsedMetadataLayers, metadataValuesByWell, plateMetadataFields, plateSchemaFieldValues} =
    useMemo(() => {
      if (
        accessibleMetadataDataFrame?.dataFrameJson == null ||
        accessibleMetadataDataFrame.allColumnsJson == null
      ) {
        return {
          parsedMetadataLayers: undefined,
          metadataValuesByWell: undefined,
          plateMetadataFields: [],
          plateSchemaFieldValues: [],
        };
      }
      const {dataFrameJson} = accessibleMetadataDataFrame;
      const cols = accessibleMetadataDataFrame.allColumnsJson as DataFrame['columns'];
      const {wellLayerColumns, plateFieldColumns} = partitionMetadataColumns(cols);
      const layers = columnsToLayerInfo(wellLayerColumns);
      const values = parseMetadataValues(dataFrameJson, layers);

      const schemaFieldSystemNames = new Set(
        (plateSchema?.fields ?? []).map((field) => buildSchemaPlateFieldSystemName(field.apiId))
      );
      const schemaFieldColumns = plateFieldColumns.filter((col) =>
        schemaFieldSystemNames.has(col.systemName)
      );
      const adHocFieldColumns = plateFieldColumns.filter(
        (col) => !schemaFieldSystemNames.has(col.systemName)
      );

      return {
        parsedMetadataLayers: layers,
        metadataValuesByWell: values,
        plateMetadataFields: parsePlateFieldValues(dataFrameJson, columnsToLayerInfo(adHocFieldColumns)),
        plateSchemaFieldValues: parsePlateFieldValues(dataFrameJson, columnsToLayerInfo(schemaFieldColumns)),
      };
    }, [accessibleMetadataDataFrame, plateSchema]);

  const {parsedParameterLayers, parameterValuesByWell} = useMemo(() => {
    if (accessibleParameterDataFrame?.dataFrameJson == null) {
      return {parsedParameterLayers: undefined, parameterValuesByWell: undefined};
    }
    const rows = accessibleParameterDataFrame.dataFrameJson.rows;
    const layers = parseParameterLayerInfo(rows);
    const values = parseParameterValues(rows, layers);
    return {parsedParameterLayers: layers, parameterValuesByWell: values};
  }, [accessibleParameterDataFrame]);

  const plateMap = useMemo(() => {
    if (plate == null) {
      return null;
    }
    return buildPlateMap(
      plate,
      accessibleLabelDataFrame?.dataFrameJson,
      concentrationRows,
      unitsByApiId,
      parsedMetadataLayers,
      metadataValuesByWell,
      parsedParameterLayers,
      parameterValuesByWell
    );
  }, [
    plate,
    accessibleLabelDataFrame,
    concentrationRows,
    unitsByApiId,
    parsedMetadataLayers,
    metadataValuesByWell,
    parsedParameterLayers,
    parameterValuesByWell,
  ]);

  const containerSchemaId = plateSchema?.containerSchemaId ?? null;

  return {
    scratchPlateQuery,
    plate,
    plateMap,
    containerSchemaId,
    plateSchema,
    dataFrameIsInaccessible,
    dataFrameQueriesPending,
    dataFrameQueriesError,
    schemaStorePending,
    schemaStoreLoadFailed,
    plateMetadataFields,
    plateSchemaFieldValues,
    refetch: scratchPlateQuery.refetch,
    refetchDataFrames,
  };
}

interface ScratchPlateCreatedPlatesData {
  createdPlates: Array<ScratchPlateCreatedPlate>;
  refetch: () => void;
}

export function useScratchPlateCreatedPlates(
  scratchPlateId: string,
  shouldQueryCreatedPlates: boolean = true
): ScratchPlateCreatedPlatesData {
  const scratchPlateCreatedPlatesQuery = useSkippableBenchlingQuery<
    GetScratchPlateCreatedPlates,
    GetScratchPlateCreatedPlatesVariables
  >(GET_SCRATCH_PLATE_CREATED_PLATES_QUERY, {
    skip: !shouldQueryCreatedPlates,
    variables: {id: scratchPlateId},
  });

  // We intentionally collapse loading/error/inaccessible states to an empty list:
  // `ScratchPlateDesignSummary` uses the design query as the source of truth for
  // access/error messaging, while this query only controls whether the records
  // toggle appears.
  const createdPlates =
    scratchPlateCreatedPlatesQuery.type === QueryResultType.SUCCESS &&
    scratchPlateCreatedPlatesQuery.data.scratchPlate.__typename === 'ScratchPlateNode'
      ? scratchPlateCreatedPlatesQuery.data.scratchPlate.createdPlates
      : [];

  return {
    createdPlates,
    refetch: scratchPlateCreatedPlatesQuery.refetch,
  };
}

/**
 * Filter `wellSchemaLayers` down to the fields that actually have a
 * non-null value on at least one well.
 */
export function useWellSchemaOptionsForPlateMap(
  plateMap: PlateMap | null,
  wellSchemaLayers: Array<SchematizedDisplayLayerMetadata> | null
): Array<SchematizedDisplayLayerMetadata> {
  return useMemo((): Array<SchematizedDisplayLayerMetadata> => {
    if (wellSchemaLayers == null || plateMap == null) {
      return [];
    }
    const populatedFieldIds = new Set(
      plateMap.wells.flatMap((well) =>
        Object.entries(well.schemaTags)
          .filter(([, tag]) => {
            const v =
              tag.floatValue ??
              tag.integerValue ??
              tag.booleanValue ??
              tag.value ??
              tag.dateValue ??
              tag.datetimeValue ??
              tag.schemaFieldSelectorOptionIds;
            return v != null;
          })
          .map(([apiId]) => apiId)
      )
    );
    return wellSchemaLayers.filter((layer) => populatedFieldIds.has(layer.attribute.key));
  }, [wellSchemaLayers, plateMap]);
}
